/**
 * Editor for a set of design-space parameters (the laser parameters of a
 * laser config, the cluster parameters of an exploration config).
 *
 * The optimizer always searches an explicit list of allowed values. A
 * parameter is either optimised over that list or fixed at its default
 * value. The list is edited either as a range (min, max, step: the values
 * are regenerated on every keystroke that makes a valid range) or value by
 * value; the stored `continuous` flag only remembers which editor was used.
 *
 * An invalid entry (not a number, step of zero, min above max, a range of
 * more than MAX_VALUES values) stays in its field with the error, leaves the
 * values unchanged and is counted through `onInvalidChange`; nothing is
 * clamped. The backend refuses values outside the laser's hardware limits.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingFields, { useInvalidTotal } from "../pages/colorExploration/setup/SettingFields";
import { HAIRLINE, INK } from "./cardChrome";

export const MAX_VALUES = 50000;

export function deriveStep(values) {
  if (!Array.isArray(values) || values.length < 2) return 1;
  const raw = (values[values.length - 1] - values[0]) / (values.length - 1);
  return parseFloat(raw.toPrecision(6));
}

// limit_min / limit_max are derived from the allowed values so they can never
// go stale when the values are edited. The chromosome bit width is an internal
// GA detail computed in the backend at runtime and must NOT appear in configs:
// updateEncoding also deletes a stale `bits` key from edited parameters.
export function updateEncoding(param, values) {
  const updated = { ...param };
  delete updated.bits;
  if (!Array.isArray(values) || values.length === 0) return updated;
  updated.limit_min = Math.min(...values);
  updated.limit_max = Math.max(...values);
  return updated;
}

const decimalsOf = (text) => (String(text).split(".")[1] || "").length;

export function generateRange(min, max, step) {
  const fMin = Number(min), fMax = Number(max), fStep = Number(step);
  if (fStep <= 0 || fMin > fMax) return [];
  // floor, so no generated value ever lies above the typed max
  const count = Math.floor((fMax - fMin) / fStep + 1e-9) + 1;
  const decimals = Math.max(decimalsOf(step), decimalsOf(min), 1);
  return Array.from({ length: count }, (_, i) => parseFloat((fMin + i * fStep).toFixed(decimals)));
}

const parse = (text) => {
  const trimmed = String(text ?? "").trim();
  if (trimmed === "") return null;
  const value = Number(trimmed.replace(",", "."));
  return Number.isFinite(value) ? value : null;
};

/** The errors of a typed range, per field (empty when the range is valid). */
export function rangeErrors({ min, max, step }) {
  const errors = {};
  const fMin = parse(min), fMax = parse(max), fStep = parse(step);
  if (fMin === null) errors.min = "Not a number";
  else if (fMin < 0) errors.min = "At least 0";
  if (fMax === null) errors.max = "Not a number";
  if (fStep === null) errors.step = "Not a number";
  else if (fStep <= 0) errors.step = "Above 0";
  if (Object.keys(errors).length) return errors;
  if (fMin > fMax) {
    errors.max = "Below min";
    return errors;
  }
  const count = Math.floor((fMax - fMin) / fStep + 1e-9) + 1;
  if (count > MAX_VALUES) errors.step = `Too many values (${count})`;
  return errors;
}

const humanName = (name) => name.replace(/_/g, " ");

function RangeFields({ param, onChange, onInvalidChange, testPrefix }) {
  const values = Array.isArray(param.values) ? param.values : [];
  const derived = {
    min: String(values.length ? values[0] : param.limit_min ?? 0),
    max: String(values.length ? values[values.length - 1] : param.limit_max ?? 1),
    step: String(deriveStep(values)),
  };
  // What is typed, kept while the values are the ones it was typed against.
  const [draft, setDraft] = useState(null);
  const text = draft && draft.forValues === param.values ? draft.text : derived;
  const errors = rangeErrors(text);
  const invalid = Object.keys(errors).length > 0;
  const reported = useRef(null);
  useEffect(() => {
    const count = invalid ? 1 : 0;
    if (reported.current !== count) {
      reported.current = count;
      onInvalidChange(count);
    }
  }, [invalid, onInvalidChange]);
  useEffect(() => () => onInvalidChange(0), [onInvalidChange]);

  const handleChange = (key) => (event) => {
    const next = { ...text, [key]: event.target.value };
    if (Object.keys(rangeErrors(next)).length === 0) {
      const newValues = generateRange(next.min, next.max, next.step);
      setDraft({ text: next, forValues: newValues });
      onChange({ ...updateEncoding(param, newValues), values: newValues });
    } else {
      setDraft({ text: next, forValues: param.values });
    }
  };

  return (
    <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: "repeat(3, minmax(0, 1fr))", maxWidth: 440 }}>
      {[
        ["min", "Min"],
        ["max", "Max"],
        ["step", "Step"],
      ].map(([key, label]) => (
        <TextField
          key={key}
          label={label}
          size="small"
          value={text[key]}
          onChange={handleChange(key)}
          error={Boolean(errors[key])}
          helperText={errors[key] || " "}
          inputProps={{ inputMode: "decimal", "data-testid": `${testPrefix}-${key}` }}
          sx={{ width: "100%" }}
        />
      ))}
    </Box>
  );
}

function ListFields({ param, onChange, onInvalidChange, testPrefix }) {
  const setterFor = useInvalidTotal(onInvalidChange);
  const values = Array.isArray(param.values) ? param.values : [];
  const energies = Array.isArray(param.nominal_pulse_energies) ? param.nominal_pulse_energies : null;

  const commit = (next) => onChange({ ...updateEncoding(next, next.values), values: next.values });
  const handleAdd = () => {
    const updates = { ...param, values: [...values, 0] };
    if (energies) updates.nominal_pulse_energies = [...energies, 0];
    commit(updates);
  };
  const handleRemove = (i) => {
    const updates = { ...param, values: values.filter((_, idx) => idx !== i) };
    if (energies) updates.nominal_pulse_energies = energies.filter((_, idx) => idx !== i);
    commit(updates);
  };
  const rowFields = (i) => {
    const fields = [{ path: ["values", i], label: `Value ${i + 1}`, min: 0 }];
    if (energies) fields.push({ path: ["nominal_pulse_energies", i], label: `Pulse energy ${i + 1}`, min: 0, unit: "mJ" });
    return fields;
  };

  return (
    <Stack spacing={1} alignItems="flex-start" sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "grid",
          gap: 1,
          gridTemplateColumns: energies
            ? { xs: "minmax(0, 1fr) auto", md: "repeat(2, minmax(0, 1fr) auto)" }
            : { xs: "repeat(2, minmax(0, 1fr) auto)", md: "repeat(4, minmax(0, 1fr) auto)" },
          alignItems: "start",
          width: "100%",
        }}
      >
        {values.map((_, i) => (
          <React.Fragment key={i}>
            <SettingFields
              config={param}
              fields={rowFields(i)}
              onChange={commit}
              onInvalidChange={setterFor(String(i))}
              testPrefix={testPrefix}
              columns={energies ? 2 : 1}
            />
            <Tooltip title="Remove">
              <IconButton size="small" onClick={() => handleRemove(i)} sx={{ mt: 0.5 }} aria-label={`Remove value ${i + 1}`}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </React.Fragment>
        ))}
      </Box>
      <Button size="small" startIcon={<AddIcon />} onClick={handleAdd} sx={{ textTransform: "none" }}>
        Add value
      </Button>
    </Stack>
  );
}

function ParameterRow({ name, param, onChange, onInvalidChange, showCalibration, testPrefix }) {
  const setterFor = useInvalidTotal(onInvalidChange);
  const values = Array.isArray(param.values) ? param.values : [];
  const asList = param.continuous === 0;
  const optimised = Boolean(param.optimize);
  const scalarFields = [];
  if (!optimised) {
    scalarFields.push({ path: ["default_value"], label: "Default value", help: "Marked at this value, as it is not optimised" });
  }
  if (showCalibration) {
    scalarFields.push({ path: ["calibration_value"], label: "Calibration value", help: "Header, labels and crosses" });
  }

  return (
    <Box
      data-testid={`${testPrefix}-parameter`}
      sx={{
        px: 2,
        py: 1.75,
        borderLeft: `3px solid ${optimised ? "#1e4fa3" : "transparent"}`,
        bgcolor: optimised ? "#ffffff" : "#fbfcfd",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
        <Typography sx={{ fontWeight: 700, textTransform: "capitalize", color: INK, minWidth: 120 }}>
          {humanName(name)}
        </Typography>
        <Typography variant="caption" sx={{ color: "#64748b" }} data-testid={`${testPrefix}-count`}>
          {values.length === 1 ? "1 value" : `${values.length} values`}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={asList ? "list" : "range"}
          onChange={(_event, mode) => {
            if (mode && mode !== (asList ? "list" : "range")) onChange({ ...param, continuous: mode === "list" ? 0 : 1 });
          }}
          aria-label={`${humanName(name)} values`}
          sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 1.5, py: 0.25, fontWeight: 600 } }}
        >
          <ToggleButton value="range">Range</ToggleButton>
          <ToggleButton value="list">List</ToggleButton>
        </ToggleButtonGroup>
        <FormControlLabel
          control={
            <Switch
              checked={optimised}
              onChange={(event) => onChange({ ...param, optimize: event.target.checked })}
              size="small"
              inputProps={{ "data-testid": `${testPrefix}-optimize` }}
            />
          }
          label={<Typography variant="body2">Optimize</Typography>}
          sx={{ mr: 0 }}
        />
      </Stack>
      {asList ? (
        <ListFields param={param} onChange={onChange} onInvalidChange={setterFor("list")} testPrefix={testPrefix} />
      ) : (
        <RangeFields param={param} onChange={onChange} onInvalidChange={setterFor("range")} testPrefix={testPrefix} />
      )}
      {scalarFields.length > 0 ? (
        <Box sx={{ mt: 1, maxWidth: 440 }}>
          <SettingFields
            config={param}
            fields={scalarFields}
            onChange={onChange}
            onInvalidChange={setterFor("scalars")}
            testPrefix={testPrefix}
            columns={2}
          />
        </Box>
      ) : null}
    </Box>
  );
}

const noop = () => {};

export default function LaserDesignSpaceEditor({
  params,
  onChange,
  onInvalidChange = noop,
  showCalibration = true,
  testPrefix = "design",
}) {
  const setterFor = useInvalidTotal(onInvalidChange);
  if (!params || typeof params !== "object") {
    return (
      <Typography variant="body2" color="text.secondary">
        No design space parameters found.
      </Typography>
    );
  }

  return (
    <Box sx={{ border: `1px solid ${HAIRLINE}`, borderRadius: 3, overflow: "hidden", bgcolor: "#ffffff", width: "100%" }}>
      {Object.entries(params).map(([paramName, param], index) => {
        if (!param || typeof param !== "object") return null;
        const handleChange = (updated) => onChange({ ...params, [paramName]: updated });
        return (
          <Box key={paramName} sx={{ borderTop: index === 0 ? "none" : `1px solid ${HAIRLINE}` }}>
            <ParameterRow
              name={paramName}
              param={param}
              onChange={handleChange}
              onInvalidChange={setterFor(paramName)}
              showCalibration={showCalibration}
              testPrefix={`${testPrefix}-${paramName}`}
            />
          </Box>
        );
      })}
    </Box>
  );
}
